import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { CacheProvider } from '@emotion/react'
import { Box, Button } from '@mui/material'
import { useRegisterSW } from 'virtual:pwa-register/react'

import { store } from '@/store'

import './locales/i18n'

import AnimateButton from './components/AnimateButton'
import { JWTProvider } from './contexts/JWTContext'
import { usePWAInstallPrompt } from './hooks/usePWAInstallPrompt'
import App from './App'
import createEmotionCache from './emotionCache'

import 'react-toastify/dist/ReactToastify.css'

const intervalMS = 60 * 60 * 1000

// Register the service worker with periodic updates
const ServiceWorkerRegistration: React.FC = () => {
  const { deferredPrompt, setDeferredPrompt } = usePWAInstallPrompt()

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          window.console.log('User accepted the A2HS prompt')
        } else {
          window.console.log('User dismissed the A2HS prompt')
        }
        setDeferredPrompt(null)
      })
    }
  }

  useRegisterSW({
    onRegistered(r) {
      if (r) {
        setInterval(() => {
          r.update()
        }, intervalMS)
      }
    },
    onRegisterError(error) {
      window.console.log('SW registration error', error)
    },
  })

  if (deferredPrompt) {
    return (
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          width: '100%',
          padding: 2,
          textAlign: 'center',
          backgroundColor: '#222221',
        }}
      >
        <AnimateButton>
          <Button
            color={'info'}
            disableElevation
            onClick={handleInstallClick}
            sx={{ backgroundColor: '#EED484', color: '#222221', fontWeight: '500' }}
            variant={'contained'}
          >
            {'UYGULAMA OLARAK INDIR'}
          </Button>
        </AnimateButton>
      </Box>
    )
  }
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(
      (registration) => {
        window.console.log('SW registered: ', registration)
      },
      (error) => {
        window.console.log('SW registration failed: ', error)
      }
    )
  })
}

const nonce = import.meta.env.VITE_CSP_NONCE || 'xyz987'
const emotionCache = createEmotionCache(nonce)

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <CacheProvider value={emotionCache}>
    <Provider store={store}>
      <BrowserRouter>
        <JWTProvider>
          <App />
          <ServiceWorkerRegistration />
          <ToastContainer />
        </JWTProvider>
      </BrowserRouter>
    </Provider>
  </CacheProvider>
)
